/**
 * Brand theming for the kx design system.
 *
 * The kx tokens in kairos.css carry the Kairos Afrika palette as defaults.
 * Whitelabel deployments (teksol, cs-info-sys, msys) override them from
 * build-time env set in their own Dockerfile, so merging master into a
 * whitelabel branch never overwrites a partner's colours and there is no
 * per-brand CSS to keep in sync.
 *
 * Only VUE_APP_BRAND_PRIMARY is required. The light/deep tints and the rgb
 * triplets used by the glow and tint rules are derived from it, and each can
 * still be pinned explicitly if a brand needs an exact value.
 */

interface IRgb {
  r: number;
  g: number;
  b: number;
}

/* eslint-disable @typescript-eslint/no-var-requires */
const DEFAULT_LOGO = require('@/assets/kairos-afrika-logo.png');
/* eslint-enable @typescript-eslint/no-var-requires */

/** Display name used in copy, alt text and page titles. */
export const brandName = (): string =>
  process.env.VUE_APP_BRAND_NAME || 'Kairos Afrika';

/** The legal entity operating the service, whatever brand fronts it. */
export const OPERATING_ENTITY = 'Kairos Afrika';

/** True once a whitelabel has claimed the build via VUE_APP_BRAND_NAME. */
export const isWhitelabel = (): boolean => !!process.env.VUE_APP_BRAND_NAME;

/**
 * Brand mark image. Whitelabels set VUE_APP_BRAND_LOGO to a path served from
 * public/ (e.g. /brand/logo.png) so the asset ships with the branch without
 * overwriting the bundled Kairos mark and conflicting on every merge.
 *
 * Returns an empty string for a whitelabel that has no artwork yet, so the
 * layouts fall back to a text wordmark rather than showing the Kairos logo
 * under someone else's brand.
 */
export const brandLogo = (): string => {
  if (process.env.VUE_APP_BRAND_LOGO) {
    return process.env.VUE_APP_BRAND_LOGO;
  }
  return isWhitelabel() ? '' : DEFAULT_LOGO;
};

/**
 * Short text wordmark used when there is no logo asset. Defaults to the
 * brand name; set VUE_APP_BRAND_MARK where a shorter form reads better
 * (cs-info-sys uses "CS").
 */
export const brandMark = (): string =>
  process.env.VUE_APP_BRAND_MARK || brandName();

/** #abc and #aabbcc both parse; anything else is ignored so we keep defaults */
const parseHex = (value?: string): IRgb | null => {
  const hex = `${value ?? ''}`.trim().replace(/^#/, '');
  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((char) => char + char)
          .join('')
      : hex;
  if (!/^[0-9a-f]{6}$/i.test(full)) {
    return null;
  }
  return {
    r: parseInt(full.slice(0, 2), 16),
    g: parseInt(full.slice(2, 4), 16),
    b: parseInt(full.slice(4, 6), 16),
  };
};

const toHex = ({ r, g, b }: IRgb): string =>
  `#${[r, g, b]
    .map((channel) =>
      Math.max(0, Math.min(255, Math.round(channel)))
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;

/** ratio > 0 lightens toward white, ratio < 0 darkens toward black */
const shade = (rgb: IRgb, ratio: number): IRgb => {
  const target = ratio > 0 ? 255 : 0;
  const weight = Math.abs(ratio);
  return {
    r: rgb.r + (target - rgb.r) * weight,
    g: rgb.g + (target - rgb.g) * weight,
    b: rgb.b + (target - rgb.b) * weight,
  };
};

const asTriplet = ({ r, g, b }: IRgb): string =>
  `${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}`;

const setToken = (name: string, value?: string | null): void => {
  if (value) {
    document.documentElement.style.setProperty(name, value);
  }
};

export const applyBrandTokens = (): void => {
  if (typeof document === 'undefined') {
    return;
  }

  const primary = parseHex(process.env.VUE_APP_BRAND_PRIMARY);
  if (primary) {
    setToken('--kx-pink', toHex(primary));
    setToken('--kx-pink-rgb', asTriplet(primary));
    /* tints used for hover text and callouts, derived unless pinned */
    setToken(
      '--kx-pink-light',
      parseHex(process.env.VUE_APP_BRAND_PRIMARY_LIGHT)
        ? toHex(parseHex(process.env.VUE_APP_BRAND_PRIMARY_LIGHT) as IRgb)
        : toHex(shade(primary, 0.35))
    );
    setToken('--kx-pink-deep', toHex(shade(primary, -0.25)));
  }

  const dark = parseHex(process.env.VUE_APP_BRAND_DARK);
  if (dark) {
    setToken('--kx-navy', toHex(dark));
    setToken('--kx-navy-rgb', asTriplet(dark));
  }
};
